// @mui icons
// @mui material components
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, CircularProgress } from '@mui/material';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
// Images
// import bgImage from 'assets/images/bg-sign-in-basic.jpeg';
import loginBgVideo from 'assets/images/login-bg.mp4';
//  React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
// Authentication layout components
import BasicLayout from 'layouts/authentication/components/BasicLayout';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { brandColors } from 'types/colors';

import { changeTempPassword, getUserToken, requestResetPassword } from '../../stores/sensor/sensorStore';

const glassCardSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '34px',
  background: 'rgba(255, 255, 255, 0.14)',
  backdropFilter: 'url(#glass-distortion) blur(8px) brightness(1.05)',
  WebkitBackdropFilter: 'blur(8px) brightness(1.05)',
  boxShadow: '0 1px 1px rgba(255, 255, 255, 0.4), 0 20px 45px rgba(0, 0, 0, 0.35)',
  border: 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
    background:
      'linear-gradient(115deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.08) 18%, transparent 32%), radial-gradient(140% 90% at 100% 115%, rgba(0, 0, 0, 0.18), transparent 50%)',
    mixBlendMode: 'overlay',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    pointerEvents: 'none',
    padding: '1px',
    borderRadius: 'inherit',
    background:
      'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.1) 30%, rgba(255, 255, 255, 0.05) 60%, rgba(255, 255, 255, 0.7))',
    WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  },
  '& > *': {
    position: 'relative',
    zIndex: 2,
  },
};

const glassInputSx = {
  '& .MuiOutlinedInput-root': {
    overflow: 'hidden',
    borderRadius: '16px',
    background: 'rgba(15, 23, 42, 0.28)',
    backdropFilter: 'url(#glass-distortion) blur(8px) brightness(0.95)',
    WebkitBackdropFilter: 'blur(8px) brightness(0.95)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.24), 0 10px 24px rgba(0, 0, 0, 0.18)',
    color: '#fff',
    transition: 'background 200ms ease, box-shadow 200ms ease',
    '&:hover': {
      background: 'rgba(15, 23, 42, 0.34)',
    },
    '&.Mui-focused': {
      background: 'rgba(15, 23, 42, 0.38)',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.32), 0 12px 28px rgba(0, 0, 0, 0.24)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.28)',
      borderWidth: '1px !important',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.44)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.68)',
    },
  },
  '& .MuiInputBase-input': {
    color: '#fff',
  },
  '& .MuiInputBase-input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 100px rgba(15, 23, 42, 0.7) inset',
    WebkitTextFillColor: '#fff',
    caretColor: '#fff',
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.72)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#fff',
  },
  '& .MuiSvgIcon-root': {
    color: 'rgba(255, 255, 255, 0.82)',
  },
};

function SignIn() {
  const [user, setUser] = useState('');
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);
  const submittedForm = useRef(false);

  // local error UI state + timer ref
  const [errorMsg, setErrorMsg] = useState('');
  const [showError, setShowError] = useState(false);
  const hideTimerRef = useRef(null);

  // forgot my password
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);

  // Temp password change flow
  const [requiresTempPasswordChange, setRequiresTempPasswordChange] = useState(false);
  const [tempUserId, setTempUserId] = useState(null);
  const [tempUsername, setTempUsername] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmNewPwd, setConfirmNewPwd] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmNewPwd, setShowConfirmNewPwd] = useState(false);

  const dispatch = useDispatch();

  const setToken = async (user, pwd) => {
    try {
      const result = await dispatch(getUserToken({ user, pwd })).unwrap();

      // Check if temp password change is required
      if (result?.requiresPasswordReset) {
        setRequiresTempPasswordChange(true);
        setTempUserId(result.userId);
        setTempUsername(result.username);
        return; // Don't proceed with login
      }
    } catch (err) {
      const msg = err?.message || err || 'Login failed';
      console.error('Login error (component):', msg);

      // surface error under password input for 6s with fade
      setErrorMsg(String(msg));
      setShowError(true);

      // clear any existing timer, then schedule hide
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      hideTimerRef.current = setTimeout(() => setShowError(false), 4000);
    }
  };

  useEffect(() => {
    submittedForm.current = true;
    // cleanup timer on unmount
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();

    if (isForgotPassword) {
      // Handle password reset request
      setIsRecoveringPassword(true);
      try {
        await dispatch(requestResetPassword(email)).unwrap();
        setSuccessMsg('Password reset link sent to your email');
        setShowError(false);
        setTimeout(() => {
          setSuccessMsg('');
          setIsForgotPassword(false);
          setEmail('');
        }, 4000);
      } catch (err) {
        const msg = err?.message || 'Failed to send reset email';
        setErrorMsg(String(msg));
        setShowError(true);
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = setTimeout(() => setShowError(false), 4000);
      } finally {
        setIsRecoveringPassword(false);
      }
    } else {
      // login logic
      await setToken(user, pwd);
    }
  };

  const handleTempPasswordChange = async e => {
    e.preventDefault();

    // Validate password length
    if (newPwd.length < 6) {
      setErrorMsg('Password must be at least 6 characters long');
      setShowError(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      hideTimerRef.current = setTimeout(() => setShowError(false), 4000);
      return;
    }

    // Validate passwords match
    if (newPwd !== confirmNewPwd) {
      setErrorMsg('Passwords do not match');
      setShowError(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      hideTimerRef.current = setTimeout(() => setShowError(false), 4000);
      return;
    }

    try {
      await dispatch(
        changeTempPassword({
          userId: tempUserId,
          tempPassword: pwd,
          newPassword: newPwd,
        }),
      ).unwrap();

      // Success - now login with new password
      await setToken(tempUsername, newPwd);
    } catch (err) {
      const msg = typeof err === 'string' ? err : err?.message || 'Failed to change password';
      setErrorMsg(String(msg));
      setShowError(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      hideTimerRef.current = setTimeout(() => setShowError(false), 4000);
    }
  };

  return (
    <BasicLayout
      image=""
      sx={{
        overflow: 'hidden',
      }}>
      <Box
        component="video"
        src={loginBgVideo}
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        sx={{
          position: 'fixed',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(rgba(0, 0, 0, 0.38), rgba(0, 0, 0, 0.38))',
          zIndex: 0,
        }}
      />
      <Box
        component="svg"
        aria-hidden="true"
        focusable="false"
        sx={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
          zIndex: 0,
        }}>
        <filter id="glass-distortion" x="-25%" y="-25%" width="150%" height="150%">
          <feTurbulence type="fractalNoise" baseFrequency="0.012 0.012" numOctaves="2" seed="7" result="noise" />
          <feGaussianBlur in="noise" stdDeviation="3" result="blurred" />
          <feDisplacementMap in="SourceGraphic" in2="blurred" scale="16" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </Box>
      <Card sx={{ ...glassCardSx, zIndex: 1 }}>
        <MDBox
          className="glass-card"
          mx="auto"
          mt={0}
          p={3}
          mb={0}
          pb={1}
          textAlign="center"
          sx={{
            width: 'fit-content',
            maxWidth: '100%',
            background: 'transparent',
            textShadow: '0 1px 12px rgba(0, 0, 0, 0.35)',
          }}>
          <Box
            component="img"
            src="/logo_white.svg"
            alt="Logo"
            sx={{
              height: 90,
              marginRight: 2,
              scale: '1.4',
            }}
          />
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}></MDTypography>
        </MDBox>

        <MDBox pt={2} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={requiresTempPasswordChange ? handleTempPasswordChange : handleSubmit}>
            {requiresTempPasswordChange ? (
              // Temp Password Change Form
              <>
                <MDTypography variant="h6" mb={2}>
                  Change Your Temporary Password
                </MDTypography>
                <MDTypography variant="caption" color="text" mb={2} display="block">
                  You must change your temporary password before continuing.
                </MDTypography>

                <MDBox mb={2}>
                  <MDInput
                    value={newPwd}
                    name="newPassword"
                    type={showNewPwd ? 'text' : 'password'}
                    label="New Password (min 6 characters)"
                    onChange={e => setNewPwd(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowNewPwd(s => !s)} onMouseDown={e => e.preventDefault()} edge="end">
                            {showNewPwd ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>

                <MDBox mb={0}>
                  <MDInput
                    value={confirmNewPwd}
                    name="confirmNewPassword"
                    type={showConfirmNewPwd ? 'text' : 'password'}
                    label="Confirm New Password"
                    onChange={e => setConfirmNewPwd(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmNewPwd(s => !s)}
                            onMouseDown={e => e.preventDefault()}
                            edge="end">
                            {showConfirmNewPwd ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
              </>
            ) : isForgotPassword ? (
              // Forgot Password Form
              <MDBox mb={2}>
                <MDInput
                  value={email}
                  name="email"
                  type="email"
                  label="Email"
                  onChange={e => setEmail(e.target.value)}
                  fullWidth
                  required
                  sx={glassInputSx}
                />
              </MDBox>
            ) : (
              // Login Form
              <>
                <MDBox mb={2}>
                  <MDInput
                    value={user}
                    name="user"
                    type="text"
                    label="User"
                    onChange={e => setUser(e.target.value)}
                    fullWidth
                    required
                    sx={glassInputSx}
                  />
                </MDBox>

                <MDBox mb={0}>
                  <MDInput
                    value={pwd}
                    name="password"
                    type={show ? 'text' : 'password'}
                    label="Password"
                    onChange={e => setPwd(e.target.value)}
                    fullWidth
                    required
                    sx={glassInputSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={show ? 'Hide password' : 'Show password'}
                            onClick={() => setShow(s => !s)}
                            onMouseDown={e => e.preventDefault()}
                            edge="end">
                            {show ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
              </>
            )}

            {/* Error/Success message */}
            <MDBox
              role="alert"
              aria-live="polite"
              sx={{
                mt: showError || successMsg ? 0.5 : 0,
                maxHeight: showError || successMsg ? 48 : 0,
                opacity: showError || successMsg ? 1 : 0,
                overflow: 'hidden',
                transition: 'opacity 400ms ease, max-height 400ms ease, margin-top 400ms ease',
              }}>
              <MDTypography
                variant="caption"
                sx={{
                  color: successMsg ? 'success.main' : 'error.main',
                  display: 'block',
                  fontWeight: '600',
                  fontSize: '.9rem',
                }}>
                {successMsg || errorMsg}
              </MDTypography>
            </MDBox>
            {/* Submit Button  */}
            <MDBox mt={3} mb={1}>
              <MDButton
                type="submit"
                fullWidth
                disabled={isRecoveringPassword}
                disableRipple
                sx={{
                  borderRadius: '16px',
                  backgroundColor: brandColors.BLUE,
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  '&:hover': {
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.3)',
                    backgroundColor: `${brandColors.BLUE} !important`,
                  },
                  '&:active': {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    backgroundColor: `${brandColors.BLUE} !important`,
                  },
                  '&:focus': {
                    backgroundColor: `${brandColors.BLUE} !important`,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: `${brandColors.BLUE} !important`,
                    color: '#fff',
                    opacity: 0.75,
                  },
                }}>
                {isRecoveringPassword && <CircularProgress size={18} color="inherit" />}
                {requiresTempPasswordChange ? 'change password' : isForgotPassword ? 'recover password' : 'sign in'}
              </MDButton>
            </MDBox>
            {/* link - shown for login and forgot password states */}
            {!requiresTempPasswordChange && (
              <MDBox mt={2} textAlign="center">
                <MDTypography
                  variant="button"
                  color="text"
                  sx={{
                    cursor: 'pointer',
                    // textDecoration: 'underline',
                    color: '#fff',
                  }}
                  onClick={() => {
                    setIsForgotPassword(!isForgotPassword);
                    setErrorMsg('');
                    setSuccessMsg('');
                    setShowError(false);
                  }}>
                  {isForgotPassword ? 'Back to sign in' : 'Forgot my password'}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default SignIn;
